"use client";
import { cn } from "@/lib/utils";
import { useScrollSpy } from "./hooks";

interface GuideTocLink {
  id: string;
  label: string;
}

interface GuideTocProps {
  label: string;
  steps: GuideTocLink[];
  // The links after the numbered steps ("Try the planner", "Your checklist").
  extras: GuideTocLink[];
}

const linkClass = (isOn: boolean) =>
  cn(
    "flex items-center gap-2.5 rounded-xl px-2.5 py-2 text-[14.5px] leading-snug font-semibold transition-colors motion-reduce:transition-none",
    isOn ? "bg-tone-blush text-primary" : "text-ink-muted hover:bg-paper hover:text-ink",
  );

// "In this guide": sticky beside the steps from lg up, a plain card above
// them on smaller screens. The current section's link is highlighted.
const GuideToc = ({ label, steps, extras }: GuideTocProps) => {
  const activeId = useScrollSpy([...steps, ...extras].map((link) => link.id));
  return (
    <nav
      aria-label={label}
      className="grid gap-2.5 rounded-3xl bg-white p-5 text-ink shadow-card lg:sticky lg:top-24"
    >
      <b className="text-xs tracking-[0.08em] text-ink-muted uppercase">{label}</b>
      <ol className="grid gap-1 sm:grid-cols-2 lg:grid-cols-1">
        {steps.map((step, index) => {
          const isOn = step.id === activeId;
          return (
            <li key={step.id}>
              <a
                href={`#${step.id}`}
                aria-current={isOn ? "location" : undefined}
                className={linkClass(isOn)}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    "grid size-6 shrink-0 place-items-center rounded-full text-[12.5px] font-extrabold",
                    isOn ? "bg-primary text-white" : "bg-paper text-ink",
                  )}
                >
                  {index + 1}
                </span>
                {step.label}
              </a>
            </li>
          );
        })}
      </ol>
      <ul className="grid gap-1">
        {extras.map((extra) => {
          const isOn = extra.id === activeId;
          return (
            <li key={extra.id}>
              <a
                href={`#${extra.id}`}
                aria-current={isOn ? "location" : undefined}
                className={cn(linkClass(isOn), "pl-11 text-[13.5px]")}
              >
                {extra.label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default GuideToc;
